import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import rfidService, { ReaderType } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/config/constants';
import { TableView } from '@/components/common/Table';
import { TableHeader } from '@/components/common/Table/table.types';

const ReaderTypeList: React.FC = () => {
  const [readerTypes, setReaderTypes] = useState<ReaderType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [codeSearch, setCodeSearch] = useState<string>('');
  const [descriptionSearch, setDescriptionSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const navigate = useNavigate();

  useEffect(() => {
    fetchReaderTypes();
  }, [page, rowsPerPage, search, codeSearch, descriptionSearch, statusFilter, sortKey, sortDirection]);

  const fetchReaderTypes = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      if (search && String(search).trim()) {
        params.search = String(search).trim();
      }
      if (codeSearch && String(codeSearch).trim()) {
        params.code = String(codeSearch).trim();
      }
      if (descriptionSearch && String(descriptionSearch).trim()) {
        params.description = String(descriptionSearch).trim();
      }
      if (sortKey) {
        params.ordering = sortDirection === 'desc' ? `-${sortKey}` : sortKey;
      }
      if (statusFilter) {
        params.is_active = statusFilter;
      }
      const response = await rfidService.getReaderTypes(params);
      setReaderTypes(response.results ?? []);
      setTotalCount(response.count ?? 0);
    } catch (error) {
      console.error('Error fetching reader types:', error);
      toast.error('Failed to fetch reader types');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (key: string) => {
    setSortKey(key);
    setSortDirection((prev) =>
      sortKey === key && prev === 'asc' ? 'desc' : 'asc'
    );
  };

  const handleDelete = async (id: string | number) => {
    try {
      await rfidService.deleteReaderType(String(id));
      toast.success('Reader type deleted successfully');
      fetchReaderTypes();
    } catch (error) {
      console.error('Error deleting reader type:', error);
      toast.error('Failed to delete reader type');
    }
  };

  const getStatusChip = (isActive: boolean) => {
    return isActive ? (
      <Chip label="Active" color="success" size="small" />
    ) : (
      <Chip label="Inactive" color="error" size="small" />
    );
  };

  const statusOptions = [
    { label: 'All statuses', value: '' },
    { label: 'Active', value: 'true' },
    { label: 'Inactive', value: 'false' },
  ];

  const headerDetails: TableHeader<ReaderType>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        sortable: true,
        sortKey: 'name',
        filterValue: search,
        onFilterChange: (v) => setSearch((v as string) ?? ''),
        filterPlaceholder: 'Search name...',
        render: (row) => (
          <Link
            to={`/rfid/reader-types/${row.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        ),
      },
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        sortable: true,
        sortKey: 'code',
        filterValue: codeSearch,
        onFilterChange: (v) => setCodeSearch((v as string) ?? ''),
        filterPlaceholder: 'Search code...',
        minWidth: 120,
      },
      {
        key: 'description',
        label: 'Description',
        type: 'text',
        sortable: true,
        sortKey: 'description',
        filterValue: descriptionSearch,
        onFilterChange: (v) => setDescriptionSearch((v as string) ?? ''),
        filterPlaceholder: 'Search description...',
        render: (row) => row.description || 'No description',
        minWidth: 180,
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'select',
        sortable: true,
        sortKey: 'is_active',
        filterValue: statusFilter,
        onFilterChange: (v) => setStatusFilter((v as string) ?? ''),
        dropdownOptions: statusOptions,
        filterAllLabel: 'All statuses',
        render: (row) => getStatusChip(row.is_active),
        minWidth: 110,
      },
      {
        key: 'created_at',
        label: 'Created At',
        sortable: true,
        sortKey: 'created_at',
        render: (row) =>
          row?.created_at
            ? new Date(row.created_at).toLocaleDateString('en-US')
            : '-',
        minWidth: 120,
      },
    ],
    [search, codeSearch, descriptionSearch, statusFilter]
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          RFID Reader Types
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          component={Link}
          to="/rfid/reader-types/create"
          sx={{ mr:2 }}
        >
          Add Reader Type
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableView<ReaderType>
            data={readerTypes}
            headerDetails={headerDetails}
            loading={loading}
            emptyMessage="No reader types found"
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/rfid/reader-types/${row.id}`)}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onRowsPerPageChange={(size) => {
              setRowsPerPage(size);
              setPage(0);
            }}
            handleEdit={(id) => navigate(`/rfid/reader-types/${id}/edit`)}
            handleDelete={handleDelete}
            confirmDelete={{
              title: 'Delete reader type?',
              message:
                'Are you sure you want to delete this reader type? This action cannot be undone.',
              confirmLabel: 'Delete',
              cancelLabel: 'Cancel',
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReaderTypeList;
